import { type PropertyCard } from '@resolute/schema';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';


import { useLocalStorage } from '@/lib/use-local-storage';

interface WishlistContextValue {
  items: PropertyCard[];
  count: number;
  has: (id: string) => boolean;
  toggle: (property: PropertyCard) => void;
  remove: (id: string) => void;
  clear: () => void;
}

const WishlistContext = createContext<WishlistContextValue | null>(null);

const MAX_ITEMS = 100;

/**
 * Guest-friendly wishlist. We store card snapshots so the /wishlist page renders
 * instantly with no API round-trip.
 */
export function WishlistProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useLocalStorage<PropertyCard[]>('rps-wishlist', []);

  const has = useCallback((id: string) => items.some((p) => p.id === id), [items]);

  const toggle = useCallback(
    (property: PropertyCard) => {
      setItems((prev) =>
        prev.some((p) => p.id === property.id)
          ? prev.filter((p) => p.id !== property.id)
          : [property, ...prev].slice(0, MAX_ITEMS),
      );
    },
    [setItems],
  );

  const remove = useCallback(
    (id: string) => setItems((prev) => prev.filter((p) => p.id !== id)),
    [setItems],
  );

  const clear = useCallback(() => setItems([]), [setItems]);

  const value = useMemo<WishlistContextValue>(
    () => ({ items, count: items.length, has, toggle, remove, clear }),
    [items, has, toggle, remove, clear],
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist(): WishlistContextValue {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within a WishlistProvider');
  return ctx;
}
